import React, { ReactNode, useEffect, useState } from 'react';
import CAppPaper from '../../../../../../../app/components/CAppPaper';
import { useTheme, SxProps, Grid, Box } from '@mui/material';
import AppCustomIcon from '../../../../../../../app/features/icon/AppIcon';
import investmentKpiLang from '../../../../../langs/investmentKpiLang';
import { useTranslation } from 'react-i18next';
import AppFormatX from '../../../../../../../app/features/format/AppFormatX';
import AppFormatPercent from '../../../../../../../app/features/format/AppFormatPercent';
import langInvestWordLang from '../../../../../langs/langInvestWordLang';
import investmentTypeLang from '../../../langs/investmentTypeLang';
import CAppInfoTooltip from '../../../../../../../app/components/CAppInfoTooltip';
import IInvestKpis from '../../../../../interfaces/IInvestKpis';

type PerformanceKpiProps = {
	kpis: IInvestKpis;
};

const CPerformanceKpi = (props: PerformanceKpiProps) => {
	const { kpis } = props;
	const { t } = useTranslation();
	const theme = useTheme();

	const [arr, setArr] = useState<number>();
	const [arrIsNs, setArrIsNs] = useState<boolean>();
	const [irr, setIrr] = useState<number>();
	const [irrIsNs, setIrrIsNs] = useState<boolean>();
	const [tvpi, setTvpi] = useState<number>();
	const [dpi, setDpi] = useState<number>();
	const [rvpi, setRvpi] = useState<number>();
	useEffect(() => {
		if (kpis) {
			setArr(kpis.arr ?? undefined);
			setArrIsNs(kpis.arrIsNs ?? undefined);
			setIrr(kpis.irr ?? undefined);
			setIrrIsNs(kpis.irrIsNs ?? undefined);
			setTvpi(kpis.tvpi ?? undefined);
			setDpi(kpis.dpi ?? undefined);
			setRvpi(kpis.rvpi ?? undefined);
		}

		return () => {
			setArr(undefined);
			setArrIsNs(undefined);
			setIrr(undefined);
			setIrrIsNs(undefined);
			setTvpi(undefined);
			setDpi(undefined);
			setRvpi(undefined);
		};
	}, [kpis]);

	const styleTitle = {
		color: theme.palette.secondary.main,
		verticalAlign: 'text-bottom',
		boxSizing: 'inherit',
		textAlign: 'left',
		alignItems: 'center',
		display: 'inline-flex',
		fontWeight: '500',
		fontSize: '1.25rem',
		'& .icon': {
			marginRight: '10px',
		},
	};
	const styleKPI = {
		padding: '30px',
		backgroundColor: theme.palette.primary.main,
		border: '0px',
		borderRadius: '10px',
		textAlign: 'left',
		'& .title': styleTitle,
		'& .list': {
			color: 'white',
			textAlign: 'left',
			alignItems: 'center',
			padding: '10px 0px 0px 0px',
			fontWeight: 'normal',
			'& .item': {
				color: 'white',
				textAlign: 'left',
				alignItems: 'center',
				padding: '6px 0px',
				fontWeight: 'normal',
				'& .value': {
					textAlign: 'right',
					fontWeight: '600',
					fontSize: '1.25rem',
				},
				'& .label': {
					textAlign: 'left',
				},
			},
			'& .item:last-child': {
				paddingBottom: '0px',
			},
		},
	} as SxProps;

	return (
		<CAppPaper id="expertise-performance-kpi-component" variant="outlined" color="primary" sx={styleKPI}>
			<Box className="title">
				<AppCustomIcon tag="kpi_performance" className="icon" /> {t(investmentTypeLang.performance.tag)}
			</Box>
			<Box className="list">
				<InvestmentTypeLinePerformanceKpi
					lineLabel={
						<>
							{t(investmentKpiLang.arr.tag)}
							<CAppInfoTooltip title={t(investmentKpiLang.arr_tooltip.tag)} />
						</>
					}
					lineValue={
						<AppFormatPercent textIfNull={t(langInvestWordLang.nc.tag)} value={arr} isNS={arrIsNs} />
					}
				/>
				<InvestmentTypeLinePerformanceKpi
					lineLabel={t(investmentKpiLang.irr.tag)}
					lineValue={
						<AppFormatPercent value={irr} isNS={irrIsNs} textIfNull={t(langInvestWordLang.nc.tag)} />
					}
				/>
				<InvestmentTypeLinePerformanceKpi
					lineLabel={
						<>
							{t(investmentKpiLang.tvpi.tag)}
							<CAppInfoTooltip title={t(investmentKpiLang.tvpi_tooltip.tag)} />
						</>
					}
					lineValue={<AppFormatX textIfNull={t(langInvestWordLang.nc.tag)} value={tvpi} />}
				/>
				<InvestmentTypeLinePerformanceKpi
					lineLabel={t(investmentKpiLang.dpi.tag)}
					lineValue={<AppFormatX textIfNull={t(langInvestWordLang.nc.tag)} value={dpi} />}
				/>
				<InvestmentTypeLinePerformanceKpi
					lineLabel={t(investmentKpiLang.rvpi.tag)}
					lineValue={<AppFormatX value={rvpi} textIfNull={t(langInvestWordLang.nc.tag)} />}
				/>
			</Box>
		</CAppPaper>
	);
};

type LinePerformanceKpiProps = {
	lineLabel: string | ReactNode;
	lineValue: string | ReactNode;
};
const InvestmentTypeLinePerformanceKpi = (props: LinePerformanceKpiProps) => {
	const { lineLabel, lineValue } = props;

	return (
		<Box className="item">
			<Grid container spacing={3}>
				<Grid item xs={8} className="label">
					{lineLabel}
				</Grid>
				<Grid item xs={4} className="value">
					{lineValue}
				</Grid>
			</Grid>
		</Box>
	);
};

export default CPerformanceKpi;
